import React, {
  ForwardRefExoticComponent,
  ComponentProps,
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
} from 'react';
import * as RCollapsible from '@radix-ui/react-collapsible';
import styles from './CollapsibleCard.module.scss';
import { withStyles, SwitchIndicator, MaterialSymbol } from 'vopay';

const cn = withStyles(styles);

type RootProps = ComponentProps<typeof RCollapsible.Root> & {
  cardStyle?: React.CSSProperties;
};

const Context = createContext({
  open: false,
});

const Collapsible: ForwardRefExoticComponent<RootProps> = React.forwardRef(
  ({ className, children, cardStyle, ...props }, ref) => {
    const [open, setOpen] = useState<boolean>(props.defaultOpen ?? false);

    useEffect(() => {
      if (props.open !== undefined) setOpen(props.open);
    }, [props.open]);

    return (
      <RCollapsible.Root
        ref={ref}
        {...props}
        open={open}
        onOpenChange={(val) => {
          setOpen(val);
          props.onOpenChange?.(val);
        }}
        className={cn('collapsibleContainer', className)}
      >
        <div className={cn('card')} style={cardStyle}>
          <Context.Provider value={{ open }}>{children}</Context.Provider>
        </div>
      </RCollapsible.Root>
    );
  },
);

type TriggerProps = Omit<ComponentProps<typeof RCollapsible.Trigger>, 'children'> & {
  indicator?: 'none' | 'switch' | 'chevron';
  children: ReactNode | ((args: { open: boolean }) => ReactNode);
  'data-testid'?: string;
};

const Trigger: ForwardRefExoticComponent<TriggerProps> = React.forwardRef(
  ({ children, className, indicator = 'switch', 'data-testid': dataTestId, ...props }, ref) => {
    const { open } = useContext(Context);

    return (
      <RCollapsible.Trigger
        data-testid={dataTestId || 'collapsibleTrigger'}
        ref={ref}
        {...props}
        className={cn('collapsibleTrigger', className)}
      >
        {typeof children === 'function' ? children({ open }) : children}

        {indicator === 'switch' && <SwitchIndicator checked={open} className={cn('indicator')} />}

        {indicator === 'chevron' && (
          <div className={cn('indicator', 'chevron', open && 'open')}>
            <MaterialSymbol icon="keyboard_arrow_down" size="18px" />
          </div>
        )}
      </RCollapsible.Trigger>
    );
  },
);

type ContentProps = ComponentProps<typeof RCollapsible.Content>;

const Content: ForwardRefExoticComponent<ContentProps> = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <RCollapsible.Content ref={ref} {...props} className={cn('collapsibleContent', className)}>
      <div className={cn('collapsibleContentInner')}>{children}</div>
    </RCollapsible.Content>
  );
});

type CompositeComponent = typeof Collapsible & {
  Trigger: typeof Trigger;
  Content: typeof Content;
};

(Collapsible as CompositeComponent).Trigger = Trigger;
(Collapsible as CompositeComponent).Content = Content;

export default Collapsible as CompositeComponent;
